import React from 'react';

import Box from '@rover/kibble/core/Box';

import type { AvatarSize, AvatarType, StyleProps } from '../Avatar';
import Avatar, { getSize } from '../Avatar';

export const MAX_IMAGES = 5;

export type Props = {
  imageUrls: string[];
  avatarType?: AvatarType;
  disabled?: boolean;
  /** How much of each avatar the next one covers, 0 to 1. */
  overlap?: number;
  avatarPropsOverrideCallback?: (imageUrl: string, index: number) => StyleProps;
} & StyleProps;

const StackedAvatar = ({
  imageUrls,
  avatarType,
  overlap,
  avatarPropsOverrideCallback,
  ...props
}: Props): JSX.Element | null => {
  const size: AvatarSize = getSize(props.size);
  const reverseImages = imageUrls
    .filter((url) => url !== 'undefined')
    .slice(0, MAX_IMAGES)
    .reverse();
  const numImages = reverseImages.length;
  if (numImages === 0) return null;

  // Without an `overlap` the stack keeps its original geometry: 1.5 avatar widths
  // whatever the count, so more avatars sit closer together. With one, every pair
  // sits the same distance apart however many there are.
  const spread =
    overlap === undefined ? size.diameter / 2 : size.diameter * (1 - overlap) * (numImages - 1);
  const fullWidth = numImages === 1 ? size.diameter : size.diameter + spread;
  const maxLeft = fullWidth - size.diameter;

  const avatars = reverseImages.map((imageUrl, index) => {
    const left = numImages === 1 ? 0 : maxLeft - (index * maxLeft) / (numImages - 1);

    return (
      <Box key={index} position="absolute" left={`${left}px`}>
        <Avatar
          imageUrl={imageUrl}
          avatarType={avatarType}
          borderRadius={999}
          objectFit="cover"
          borderWidth={2}
          borderColor="white"
          showBorder
          {...props}
          {...(avatarPropsOverrideCallback ? avatarPropsOverrideCallback(imageUrl, index) : {})}
        />
      </Box>
    );
  });

  return (
    <Box
      flexDirection="row"
      flexShrink="0"
      position="relative"
      width={`${fullWidth}px`}
      height={`${size.diameter}px`}
      data-testid="stackedAvatarContainer"
    >
      {avatars}
    </Box>
  );
};

export default StackedAvatar;
